import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Input,
  Select,
  Button,
  VStack,
  HStack,
  Heading,
  useToast,
  FormControl,
  FormLabel,
  Spinner,
  Textarea,
  Box,
  Text,
  CheckboxGroup,
  Checkbox,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  SimpleGrid,
  Card,
  CardHeader,
  CardBody,
  Badge,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Tooltip,
  Flex,
  Switch,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Alert,
  AlertIcon,
  NumberInput,
  NumberInputField,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  FormHelperText,
  IconButton,
  Divider,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay
} from '@chakra-ui/react';
import { InfoIcon, ChevronDownIcon, AddIcon } from '@chakra-ui/icons';
import { FiSave, FiPlus, FiTrash2, FiCopy } from 'react-icons/fi';
import { ArrowBackIcon, ChevronRightIcon } from '@chakra-ui/icons';
import { Link as RouterLink } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const EditContent = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headerBg = useColorModeValue('gray.50', 'gray.700');

  // Modal and dialog controls
  const { isOpen: isTranslationModalOpen, onOpen: onTranslationModalOpen, onClose: onTranslationModalClose } = useDisclosure();
  const { isOpen: isDeleteAlertOpen, onOpen: onDeleteAlertOpen, onClose: onDeleteAlertClose } = useDisclosure();
  const cancelRef = React.useRef();

  // Add the missing ref for population retry mechanism
  const populationRetryRef = React.useRef(0);

  // Get user data from session storage
  const user = JSON.parse(sessionStorage.getItem('user'));
  const tenantId = user?.tenant_ids?.[0] || null;

  // Main content state
  const [content, setContent] = useState({
    id: null,
    tenant_id: tenantId,
    site_id: '',
    slug: '',
    content_model_id: '',
    content_model_name: '',
    status: 'draft',
    translations: [],
    taxonomies: [], // Add this line
  });

  // Reference data states
  const [sites, setSites] = useState([]);
  const [locales, setLocales] = useState([]);
  const [contentModels, setContentModels] = useState([]);

  // Content model related states
  const [modelFields, setModelFields] = useState([]);
  const [modelTaxonomies, setModelTaxonomies] = useState([]);
  const [taxonomyTerms, setTaxonomyTerms] = useState({});
  const [selectedTerms, setSelectedTerms] = useState({});

  // UI states
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadingTaxonomies, setLoadingTaxonomies] = useState(false);
  const [loadingModelFields, setLoadingModelFields] = useState(false);
  const [errors, setErrors] = useState({});
  const [tagInput, setTagInput] = useState({});
  const [selectedLocaleForNewTranslation, setSelectedLocaleForNewTranslation] = useState('');
  const [translationToDelete, setTranslationToDelete] = useState(null);

  // Computed values
  const primaryTranslation = useMemo(() => content.translations[0] || null, [content.translations]);
  const existingLocaleIds = useMemo(() => content.translations.map(t => t.locale_id), [content.translations]);
  const availableLocalesForTranslation = useMemo(
    () => locales.filter(locale => !existingLocaleIds.includes(locale.id)),
    [locales, existingLocaleIds]
  );

  // FIXED: Remove all circular dependencies by simplifying dependency arrays

  // Process translations with proper custom fields initialization
  const processTranslations = useCallback((translations) => {
    return translations.map(translation => ({
      ...translation,
      custom_fields: typeof translation.custom_fields === 'object' && translation.custom_fields
        ? translation.custom_fields
        : {},
    }));
  }, []);

  // Default field value generator
  const getDefaultFieldValue = useCallback((fieldType) => {
    switch (fieldType) {
      case 'boolean': return false;
      case 'number': return '';
      case 'tags': return [];
      case 'json': return {};
      case 'multiselect': return [];
      default: return '';
    }
  }, []);

  // FIXED: Remove getDefaultFieldValue dependency to break circular reference
  const updateTranslationsWithFields = useCallback((fieldsData) => {
    setContent(prev => ({
      ...prev,
      translations: prev.translations.map(translation => {
        const existingCustomFields = translation.custom_fields || {};
        const initializedCustomFields = fieldsData.reduce((acc, field) => {
          const existingValue = existingCustomFields[field.key];
          let defaultValue;
          switch (field.type) {
            case 'boolean': defaultValue = false; break;
            case 'number': defaultValue = ''; break;
            case 'tags': defaultValue = []; break;
            case 'json': defaultValue = {}; break;
            case 'multiselect': defaultValue = []; break;
            default: defaultValue = '';
          }
          return {
            ...acc,
            [field.key]: existingValue !== undefined ? existingValue : defaultValue,
          };
        }, {});

        return {
          ...translation,
          custom_fields: initializedCustomFields,
        };
      }),
    }));
  }, []); // FIXED: Empty dependency array

  // FIXED: Remove toast dependency to prevent recreation
  const fetchTaxonomyData = useCallback(async (taxonomyIds) => {
    try {
      const allTaxonomiesResponse = await apiClient(`/taxonomies/${tenantId}`);
      
      let allTaxonomies = [];
      if (Array.isArray(allTaxonomiesResponse)) {
        allTaxonomies = allTaxonomiesResponse;
      } else if (allTaxonomiesResponse?.data && Array.isArray(allTaxonomiesResponse.data)) {
        allTaxonomies = allTaxonomiesResponse.data;
      }

      const filteredTaxonomies = allTaxonomies.filter(tax =>
        taxonomyIds.includes(tax.id) || taxonomyIds.includes(String(tax.id)) || taxonomyIds.includes(parseInt(tax.id))
      );

      console.log('Filtered taxonomies:', filteredTaxonomies);
      setModelTaxonomies(filteredTaxonomies);

      // Fetch terms for each taxonomy
      const termsPromises = filteredTaxonomies.map(async (tax) => {
        try {
          console.log(`Fetching terms for taxonomy ${tax.id} (${tax.name})`);
          const termsResponse = await apiClient(`/taxonomies/${tenantId}/${tax.id}/terms`);
          console.log(`Raw terms response for taxonomy ${tax.id}:`, termsResponse);
          
          // Extract terms array from response
          let terms = [];
          if (termsResponse?.terms && Array.isArray(termsResponse.terms)) {
            terms = termsResponse.terms;
          } else if (Array.isArray(termsResponse)) {
            terms = termsResponse;
          } else if (termsResponse?.data?.terms && Array.isArray(termsResponse.data.terms)) {
            terms = termsResponse.data.terms;
          } else if (termsResponse?.data && Array.isArray(termsResponse.data)) {
            terms = termsResponse.data;
          }

          console.log(`Extracted terms array for taxonomy ${tax.id}:`, terms);

          // Process terms with correct field mapping
          const processedTerms = terms.map(term => ({
            id: term.id,
            name: term.taxonomy_name || term.name || `Term ${term.id}`,
            slug: term.slug || term.taxonomy_slug || '',
            taxonomy_id: term.taxonomy_id,
            taxonomy_name: term.taxonomy_name,
            taxonomy_slug: term.taxonomy_slug,
            parent_id: term.parent_id,
            parent_slug: term.parent_slug,
            sort_order: term.sort_order || 0,
            status: term.status || 'active',
            translations: term.translations || [],
            created_at: term.created_at,
            updated_at: term.updated_at
          }));

          console.log(`Processed terms for taxonomy ${tax.id}:`, processedTerms);
          return { 
            taxonomyId: tax.id, 
            taxonomyName: tax.name,
            terms: processedTerms 
          };
        } catch (error) {
          console.error(`Error fetching terms for taxonomy ${tax.id} (${tax.name}):`, error);
          return { 
            taxonomyId: tax.id, 
            taxonomyName: tax.name,
            terms: [] 
          };
        }
      });

      const termsResults = await Promise.all(termsPromises);
      const termsMap = termsResults.reduce((acc, result) => {
        console.log(`Setting terms for taxonomy ${result.taxonomyId} (${result.taxonomyName}):`, result.terms);
        return {
          ...acc,
          [result.taxonomyId]: result.terms,
        };
      }, {});

      console.log('Final terms map:', termsMap);
      setTaxonomyTerms(termsMap);
      
    } catch (error) {
      console.error('Error fetching taxonomy data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load taxonomy data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, [tenantId]); // FIXED: Only tenantId dependency

  // FIXED: Remove function dependencies to prevent circular references
  const fetchContentModelData = useCallback(async (modelId) => {
    try {
      setLoadingModelFields(true);
      setLoadingTaxonomies(true);

      const modelResponse = await apiClient(`/content-models/${tenantId}/${modelId}`);

      // Process fields
      if (modelResponse.fields && Array.isArray(modelResponse.fields)) {
        const fieldsData = modelResponse.fields.map(field => ({
          key: field.name.toLowerCase().replace(/\s+/g, '_'),
          label: field.name,
          type: field.type,
          options: field.options || [],
        }));
        setModelFields(fieldsData);
        
        // Call updateTranslationsWithFields directly instead of as dependency
        updateTranslationsWithFields(fieldsData);
      } else {
        setModelFields([]);
      }

      // Process taxonomies
      if (modelResponse.taxonomy_ids && Array.isArray(modelResponse.taxonomy_ids)) {
        await fetchTaxonomyData(modelResponse.taxonomy_ids);
      } else {
        setModelTaxonomies([]);
        setTaxonomyTerms({});
      }
    } catch (error) {
      console.error('Error fetching content model data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load content model data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingModelFields(false);
      setLoadingTaxonomies(false);
    }
  }, [tenantId]); // FIXED: Only tenantId dependency

  // FIXED: Simplify dependencies
  const fetchInitialData = useCallback(async () => {
      try {
        setLoading(true);
        const [contentData, sitesData, localesData, contentModelsData] = await Promise.all([
          apiClient(`/content-entries/${tenantId}/${id}`),
          apiClient(`/sites/${tenantId}`),
          apiClient(`/locales/${tenantId}`),
          apiClient(`/content-models/${tenantId}`),
        ]);
  
        const rawContent = contentData.data || contentData;
        const processedContent = {
          id: rawContent?.id || id,
          tenant_id: tenantId,
          site_id: rawContent?.site_id || '',
          slug: rawContent?.slug || '',
          content_model_id: rawContent?.content_model_id || '',
          content_model_name: rawContent?.content_model_name || '',
          status: rawContent?.status || rawContent?.translations?.[0]?.status || 'draft',
          translations: rawContent?.translations || [],
          taxonomies: rawContent?.taxonomies || [],
        };
  
        setContent(processedContent);
        setSites(sitesData || []);
        setLocales(localesData || []);
        setContentModels(contentModelsData || []);
  
        if (rawContent?.content_model_id) {
          await fetchContentModelData(rawContent.content_model_id);
        }
  
        const initialSelectedTerms = {};
        (processedContent.taxonomies || []).forEach(tax => {
          const key = tax.taxonomy_id?.toString() || tax.name || tax.slug;
          initialSelectedTerms[key] = (tax.term_ids || []).map(id => id.toString());
        });
        setSelectedTerms(initialSelectedTerms);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, [tenantId, id, fetchContentModelData]);
  
    useEffect(() => {
      fetchInitialData();
    }, [fetchInitialData]);

  // REMOVE the problematic useEffect that monitors taxonomy changes
  // This was causing the infinite loop

  // Handle content model change
  const handleContentModelChange = useCallback(async (newModelId) => {
    try {
      setModelFields([]);
      setModelTaxonomies([]);
      setTaxonomyTerms({});
      setSelectedTerms({});
      await fetchContentModelData(newModelId);
    } catch (error) {
      console.error('Error changing content model:', error);
      toast({
        title: 'Error',
        description: 'Failed to load new content model data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }, []); // FIXED: Empty dependency array

  // Handle content changes
  const handleContentChange = useCallback((field, value) => {
    setContent(prev => ({ ...prev, [field]: value }));
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }

    if (field === 'content_model_id' && value && value !== content.content_model_id) {
      handleContentModelChange(value);
    }
  }, []); // FIXED: Empty dependency array

  const handleTranslationChange = useCallback((translationIndex, field, value) => {
    setContent(prev => ({
      ...prev,
      translations: prev.translations.map((translation, index) =>
        index === translationIndex ? { ...translation, [field]: value } : translation
      ),
    }));

    // Auto-update slug from primary translation title
    if (translationIndex === 0 && field === 'title' && value) {
      const newSlug = value
        .toLowerCase()
        .replace(/[^a-z0-9 -]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim();
      setContent(prev => ({ ...prev, slug: newSlug }));
    }
  }, []);

  const handleCustomFieldChange = useCallback((translationIndex, fieldKey, value) => {
    setContent(prev => ({
      ...prev,
      translations: prev.translations.map((translation, index) =>
        index === translationIndex
          ? {
              ...translation,
              custom_fields: {
                ...translation.custom_fields,
                [fieldKey]: value,
              },
            }
          : translation
      ),
    }));
  }, []);

  // Translation management
  const addTranslation = useCallback((localeId) => {
    const locale = locales.find(l => l.id.toString() === localeId.toString());
    if (!locale) return;

    const initialCustomFields = modelFields.reduce((acc, field) => ({
      ...acc,
      [field.key]: getDefaultFieldValue(field.type),
    }), {});

    const newTranslation = {
      locale_id: parseInt(localeId),
      title: '',
      content: '',
      excerpt: '',
      meta_title: '',
      meta_description: '',
      custom_fields: initialCustomFields,
      status: 'draft',
    };

    setContent(prev => ({
      ...prev,
      translations: [...prev.translations, newTranslation],
    }));

    onTranslationModalClose();
    setSelectedLocaleForNewTranslation('');

    toast({
      title: 'Translation Added',
      description: `Added translation for ${locale.name}`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }, [locales, modelFields, getDefaultFieldValue, onTranslationModalClose, toast]);

  const removeTranslation = useCallback((translationIndex) => {
    if (content.translations.length === 1) {
      toast({
        title: 'Cannot remove translation',
        description: 'At least one translation is required',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const translationToRemove = content.translations[translationIndex];
    setContent(prev => ({
      ...prev,
      translations: prev.translations.filter((_, index) => index !== translationIndex),
    }));

    const locale = locales.find(l => l.id === translationToRemove.locale_id);
    toast({
      title: 'Translation Removed',
      description: `Removed translation for ${locale ? locale.name : 'Unknown locale'}`,
      status: 'info',
      duration: 3000,
      isClosable: true,
    });
  }, [content.translations, locales, toast]);

  const duplicateTranslation = useCallback((sourceIndex, targetLocaleId) => {
    const sourceTranslation = content.translations[sourceIndex];
    const targetLocale = locales.find(l => l.id.toString() === targetLocaleId.toString());
    
    if (!sourceTranslation || !targetLocale) return;

    const duplicatedTranslation = {
      ...sourceTranslation,
      locale_id: parseInt(targetLocaleId),
      status: 'draft',
    };

    setContent(prev => ({
      ...prev,
      translations: [...prev.translations, duplicatedTranslation],
    }));

    toast({
      title: 'Translation Duplicated',
      description: `Duplicated content to ${targetLocale.name}`,
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  }, [content.translations, locales, toast]);

  // Tags management
  const addTag = useCallback((translationIndex, fieldKey) => {
    const inputKey = `${translationIndex}_${fieldKey}`;
    const inputValue = tagInput[inputKey];
    if (inputValue && inputValue.trim()) {
      const currentTags = content.translations[translationIndex]?.custom_fields?.[fieldKey] || [];
      if (!currentTags.includes(inputValue.trim())) {
        handleCustomFieldChange(translationIndex, fieldKey, [...currentTags, inputValue.trim()]);
      }
      setTagInput(prev => ({ ...prev, [inputKey]: '' }));
    }
  }, [tagInput, content.translations, handleCustomFieldChange]);

  const removeTag = useCallback((translationIndex, fieldKey, tagToRemove) => {
    const currentTags = content.translations[translationIndex]?.custom_fields?.[fieldKey] || [];
    handleCustomFieldChange(
      translationIndex,
      fieldKey,
      currentTags.filter(tag => tag !== tagToRemove)
    );
  }, [content.translations, handleCustomFieldChange]);

  const handleTagInputChange = useCallback((translationIndex, fieldKey, value) => {
    const inputKey = `${translationIndex}_${fieldKey}`;
    setTagInput(prev => ({ ...prev, [inputKey]: value }));
  }, []);

  const handleTagInputKeyPress = useCallback((translationIndex, fieldKey, e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(translationIndex, fieldKey);
    }
  }, [addTag]);

  // Taxonomy management
  const handleTermsChange = useCallback((taxonomyId, selectedTermIds) => {
    console.log('handleTermsChange called:', {
      taxonomyId,
      selectedTermIds,
      currentSelectedTerms: selectedTerms
    });
    
    setSelectedTerms(prev => {
      const updated = {
        ...prev,
        [taxonomyId.toString()]: selectedTermIds.map(id => id.toString())

      };
      console.log('Updated selectedTerms:', updated);
      return updated;
    });

    // FIXED: Also update content.taxonomies to reflect the changes
    setContent(prevContent => {
      const updatedTaxonomies = prevContent.taxonomies.map(contentTax => {
        // Check if this is the taxonomy we're updating
        const taxonomy = modelTaxonomies.find(tax => tax.id.toString() === taxonomyId.toString());
        if (!taxonomy) return contentTax;

        if (contentTax.name === taxonomy.name || 
            contentTax.slug === taxonomy.slug ||
            contentTax.taxonomy_name === taxonomy.name) {
          
          // Convert selectedTermIds back to numbers for content.taxonomies
          const termIdsAsNumbers = selectedTermIds.map(id => parseInt(id)).filter(id => !isNaN(id));
          
          return {
            ...contentTax,
            term_ids: termIdsAsNumbers
          };
        }
        return contentTax;
      });

      // If no existing taxonomy found, create a new one
      const existingTaxIndex = updatedTaxonomies.findIndex(contentTax => {
        const taxonomy = modelTaxonomies.find(tax => tax.id.toString() === taxonomyId.toString());
        if (!taxonomy) return false;
        
        return contentTax.name === taxonomy.name || 
               contentTax.slug === taxonomy.slug ||
               contentTax.taxonomy_name === taxonomy.name;
      });

      if (existingTaxIndex === -1 && selectedTermIds.length > 0) {
        const taxonomy = modelTaxonomies.find(tax => tax.id.toString() === taxonomyId.toString());
        if (taxonomy) {
          const termIdsAsNumbers = selectedTermIds.map(id => parseInt(id)).filter(id => !isNaN(id));
          updatedTaxonomies.push({
            name: taxonomy.name,
            slug: taxonomy.slug,
            taxonomy_name: taxonomy.name,
            taxonomy_slug: taxonomy.slug,
            term_ids: termIdsAsNumbers
          });
        }
      }

      return {
        ...prevContent,
        taxonomies: updatedTaxonomies.filter(tax => 
          tax.term_ids && tax.term_ids.length > 0 // Remove taxonomies with no selected terms
        )
      };
    });
  }, [selectedTerms, modelTaxonomies]);

  // Validation
  const validate = useCallback(() => {
    const newErrors = {};

    if (!content.site_id) newErrors.site_id = 'Site is required';
    if (!content.content_model_id) newErrors.content_model_id = 'Content model is required';
    if (!content.slug) newErrors.slug = 'Slug is required';

    if (!content.translations || content.translations.length === 0) {
      newErrors.translations = 'At least one translation is required';
    } else {
      const firstTranslation = content.translations[0];
      if (!firstTranslation.locale_id) newErrors.locale_id = 'Locale is required for the primary translation';
      if (!firstTranslation.title) newErrors.title = 'Title is required for the primary translation';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [content]);

  // Submit form
  const handleSubmit = useCallback(async () => {
    if (!validate()) {
      toast({
        title: 'Validation Error',
        description: 'Please fill out all required fields',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    try {
      setSaving(true);

      // Prepare taxonomy data
      const taxonomyData = Object.entries(selectedTerms)
        .map(([taxonomyId, termIds]) => {
          if (!termIds || termIds.length === 0) return null;

          const taxonomy = modelTaxonomies.find(tax => tax.id.toString() === taxonomyId.toString());
          if (!taxonomy) return null;

          const termsForTaxonomy = taxonomyTerms[taxonomyId] || [];
          const selectedTermsData = termIds
            .map(termId => {
              const term = termsForTaxonomy.find(t => t.id.toString() === termId.toString());
              return term || null;
            })
            .filter(Boolean);

          if (selectedTermsData.length === 0) return null;

          const termSlugs = selectedTermsData.map(term => term.slug).filter(Boolean);

          return {
            name: taxonomy.name,
            slug: taxonomy.slug,
            value: termSlugs,
          };
        })
        .filter(Boolean);

      // Prepare submission data
      const submitData = {
        tenant_id: tenantId,
        site_id: content.site_id,
        slug: content.slug,
        content_model_id: content.content_model_id,
        status: content.status,
        translations: content.translations.filter(t => t.locale_id && t.title),
        taxonomies: taxonomyData,
      };

      await apiClient(`/content-entries/${tenantId}/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      toast({
        title: 'Success',
        description: 'Content updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      navigate('/content');
    } catch (error) {
      console.error('Error updating content:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to update content',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  }, [validate, content, selectedTerms, modelTaxonomies, taxonomyTerms, tenantId, id, navigate, toast]);

  // Custom field renderer
  const renderCustomField = useCallback((field, translationIndex) => {
    const translation = content.translations[translationIndex];
    const currentValue = translation?.custom_fields?.[field.key] !== undefined
      ? translation.custom_fields[field.key]
      : getDefaultFieldValue(field.type);

    switch (field.type) {
      case 'text':
        return (
          <Input
            value={currentValue || ''}
            onChange={e => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
          />
        );

      case 'textarea':
        return (
          <Textarea
            value={currentValue || ''}
            onChange={e => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
            minH="120px"
            resize="vertical"
          />
        );

      case 'richtext':
        return (
          <VStack align="stretch" spacing={2}>
            <Textarea
              value={currentValue || ''}
              onChange={e => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
              placeholder={`Enter rich text content for ${field.label}`}
              minH="200px"
              resize="vertical"
              fontFamily="monospace"
              fontSize="sm"
            />
            <FormHelperText>Rich text editor - supports HTML markup</FormHelperText>
          </VStack>
        );

      case 'number':
        return (
          <NumberInput
            value={currentValue || ''}
            onChange={valueString => handleCustomFieldChange(translationIndex, field.key, valueString)}
          >
            <NumberInputField placeholder={`Enter ${field.label}`} />
          </NumberInput>
        );

      case 'boolean':
        return (
          <Switch
            isChecked={Boolean(currentValue)}
            onChange={e => handleCustomFieldChange(translationIndex, field.key, e.target.checked)}
          />
        );

      case 'tags':
        const inputKey = `${translationIndex}_${field.key}`;
        const tagsArray = Array.isArray(currentValue) ? currentValue : [];

        return (
          <VStack align="stretch" spacing={3}>
            <HStack>
              <Input
                value={tagInput[inputKey] || ''}
                onChange={e => handleTagInputChange(translationIndex, field.key, e.target.value)}
                onKeyPress={e => handleTagInputKeyPress(translationIndex, field.key, e)}
                placeholder={`Add tags for ${field.label}`}
              />
              <Button
                size="sm"
                colorScheme="blue"
                leftIcon={<FiPlus />}
                onClick={() => addTag(translationIndex, field.key)}
                isDisabled={!tagInput[inputKey]?.trim()}
              >
                Add
              </Button>
            </HStack>

            {tagsArray.length > 0 && (
              <Box>
                <Text fontSize="sm" mb={2} fontWeight="medium">Tags:</Text>
                <Wrap>
                  {tagsArray.map((tag, tagIndex) => (
                    <WrapItem key={`${tag}-${tagIndex}`}>
                      <Tag size="md" borderRadius="full" variant="solid" colorScheme="blue">
                        <TagLabel>{tag}</TagLabel>
                        <TagCloseButton onClick={() => removeTag(translationIndex, field.key, tag)} />
                      </Tag>
                    </WrapItem>
                  ))}
                </Wrap>
              </Box>
            )}
            <FormHelperText>Press Enter or click Add to add tags</FormHelperText>
          </VStack>
        );

      case 'select':
        return (
          <Select
            value={currentValue || ''}
            onChange={e => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
            placeholder={`Select ${field.label}`}
          >
            {field.options?.map(option => (
              <option key={option.value || option} value={option.value || option}>
                {option.label || option}
              </option>
            ))}
          </Select>
        );

      case 'json':
        return (
          <VStack align="stretch" spacing={2}>
            <Textarea
              value={typeof currentValue === 'string' ? currentValue : JSON.stringify(currentValue, null, 2)}
              onChange={e => {
                let jsonValue;
                try {
                  jsonValue = JSON.parse(e.target.value);
                } catch {
                  jsonValue = e.target.value;
                }
                handleCustomFieldChange(translationIndex, field.key, jsonValue);
              }}
              placeholder={`Enter JSON for ${field.label}`}
              minH="120px"
              resize="vertical"
              fontFamily="monospace"
              fontSize="sm"
            />
            <FormHelperText>Enter valid JSON data</FormHelperText>
          </VStack>
        );

      default:
        return (
          <Input
            value={currentValue || ''}
            onChange={e => handleCustomFieldChange(translationIndex, field.key, e.target.value)}
            placeholder={`Enter ${field.label}`}
          />
        );
    }
  }, [content.translations, getDefaultFieldValue, handleCustomFieldChange, tagInput, handleTagInputChange, handleTagInputKeyPress, addTag, removeTag]);

  // Utility functions
  const getLocaleName = useCallback((localeId) => {
    const locale = locales.find(l => l.id === localeId);
    return locale ? `${locale.name} (${locale.code})` : 'Unknown Locale';
  }, [locales]);

  const getTermDisplayName = useCallback((term, localeId = null) => {
    if (!term) return 'Unknown Term';
    
    // If we have translations, try to find the right one
    if (term.translations && Array.isArray(term.translations) && term.translations.length > 0) {
      const targetLocaleId = localeId || primaryTranslation?.locale_id;
      
      if (targetLocaleId) {
        // First try to find exact locale match
        const termTranslation = term.translations.find(t =>
          t.locale_id === targetLocaleId || 
          t.locale_id === String(targetLocaleId) ||
          t.locale_id === parseInt(targetLocaleId)
        );
        
        if (termTranslation && termTranslation.name) {
          return termTranslation.name;
        }
      }
      
      // Fallback to first translation
      const firstTranslation = term.translations[0];
      if (firstTranslation && firstTranslation.name) {
        return firstTranslation.name;
      }
    }
    
    // Fallback to term's direct name properties
    return term.name || term.taxonomy_name || term.slug || term.taxonomy_slug || `Term ${term.id}`;
  }, [primaryTranslation]);

  // Loading state
  if (loading) {
    return (
      <Container maxW="container.xl">
        <VStack align="center" justify="center" height="50vh">
          <Spinner size="xl" thickness="4px" speed="0.65s" color="blue.500" />
          <Text mt={4}>Loading content...</Text>
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      {/* Breadcrumb Navigation */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/content">Content</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Edit {primaryTranslation?.title || 'Content'}</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Page Header */}
      <Flex justify="space-between" align="center" mb={6}>
        <VStack align="start" spacing={1}>
          <Heading size="lg">Edit Multilingual Content</Heading>
          <HStack spacing={2}>
            <Badge colorScheme="blue" variant="subtle">
              {content.translations.length} translation{content.translations.length !== 1 ? 's' : ''}
            </Badge>
            <Badge colorScheme="purple" variant="outline">
              {content.content_model_name || 'No model'}
            </Badge>
            <Badge colorScheme="green" variant="outline">
              {content.status}
            </Badge>
          </HStack>
        </VStack>
        <HStack spacing={4}>
          <Button as={RouterLink} to="/content" leftIcon={<ArrowBackIcon />} variant="outline">
            Back
          </Button>
          <Button
            leftIcon={<FiSave />}
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={saving}
            loadingText="Saving..."
            isDisabled={
              !content.site_id ||
              !content.content_model_id ||
              !content.slug ||
              content.translations.length === 0 ||
              !primaryTranslation?.locale_id ||
              !primaryTranslation?.title
            }
          >
            Save Changes
          </Button>
        </HStack>
      </Flex>

      <Tabs variant="enclosed" colorScheme="blue">
        <TabList>
          <Tab>Basic Information</Tab>
          <Tab>Translations ({content.translations.length})</Tab>
          {modelTaxonomies.length > 0 && <Tab>Taxonomies</Tab>}
        </TabList>

        <TabPanels>
          {/* Basic Information Tab */}
          <TabPanel>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
              <VStack align="stretch" spacing={5}>
                <Card borderColor={borderColor} variant="outline">
                  <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
                    <Heading size="sm">Content Information</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel fontSize="sm">ID</FormLabel>
                        <Input value={content.id || 'Auto-generated'} isReadOnly bg="gray.50" />
                      </FormControl>

                      <FormControl isRequired isInvalid={errors.slug}>
                        <FormLabel fontSize="sm">Slug</FormLabel>
                        <Input
                          value={content.slug}
                          onChange={e => handleContentChange('slug', e.target.value)}
                          placeholder="content-slug"
                          focusBorderColor="blue.400"
                        />
                        {errors.slug && <Text color="red.500" fontSize="sm" mt={1}>{errors.slug}</Text>}
                        <FormHelperText>URL-friendly identifier for this content</FormHelperText>
                      </FormControl>

                      <FormControl isRequired>
                        <FormLabel fontSize="sm">Primary Status</FormLabel>
                        <Select value={content.status} onChange={e => handleContentChange('status', e.target.value)}>
                          <option value="draft">Draft</option>
                          <option value="published">Published</option>
                          <option value="archived">Archived</option>
                        </Select>
                        <FormHelperText>Overall content status</FormHelperText>
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>

              <VStack align="stretch" spacing={5}>
                <Card borderColor={borderColor} variant="outline">
                  <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
                    <Heading size="sm">Site & Model</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <FormControl isRequired isInvalid={errors.site_id}>
                        <FormLabel fontSize="sm">Site</FormLabel>
                        <Select
                          value={content.site_id}
                          onChange={e => handleContentChange('site_id', e.target.value)}
                          placeholder="Select Site"
                        >
                          {console.log('Available sites:', sites)}
                          {sites.map(site => (
                            <option key={site.id} value={site.id}>{site.name}</option>
                          ))}
                        </Select>
                        {errors.site_id && <Text color="red.500" fontSize="sm" mt={1}>{errors.site_id}</Text>}
                      </FormControl>

                      <FormControl isRequired isInvalid={errors.content_model_id}>
                        <FormLabel fontSize="sm" display="flex" alignItems="center">
                          Content Model
                          <Tooltip
                            label="Content model defines the structure and fields for this content"
                            placement="top"
                            hasArrow
                          >
                            <InfoIcon ml={2} color="blue.500" fontSize="sm" />
                          </Tooltip>
                        </FormLabel>
                        <Select
                          value={content.content_model_id}
                          onChange={e => handleContentChange('content_model_id', e.target.value)}
                          placeholder="Select Content Model"
                        >
                          {contentModels.map(model => (
                            <option key={model.id} value={model.id}>{model.name}</option>
                          ))}
                        </Select>
                        {errors.content_model_id && <Text color="red.500" fontSize="sm" mt={1}>{errors.content_model_id}</Text>}
                      </FormControl>

                      <Box>
                        <Text fontSize="sm" fontWeight="medium" mb={2}>Fields & Taxonomies:</Text>
                        <HStack spacing={2} wrap="wrap">
                          <Badge colorScheme="green" variant="subtle">
                            {modelFields.length} field{modelFields.length !== 1 ? 's' : ''}
                          </Badge>
                          <Badge colorScheme="orange" variant="subtle">
                            {modelTaxonomies.length} taxonom{modelTaxonomies.length !== 1 ? 'ies' : 'y'}
                          </Badge>
                        </HStack>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>
              </VStack>
            </SimpleGrid>
          </TabPanel>

          {/* Translations Tab */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Flex justify="space-between" align="center">
                <Heading size="md">Content Translations</Heading>
                {availableLocalesForTranslation.length > 0 && (
                  <Menu>
                    <MenuButton as={Button} leftIcon={<FiPlus />} colorScheme="green" variant="outline">
                      Add Translation
                    </MenuButton>
                    <MenuList>
                      <MenuItem icon={<AddIcon />} onClick={onTranslationModalOpen}>
                        New Translation
                      </MenuItem>
                      {content.translations.length > 0 && availableLocalesForTranslation.length > 0 && (
                        <>
                          <Divider />
                          <Text px={3} py={2} fontSize="sm" color="gray.500" fontWeight="medium">
                            Duplicate from existing:
                          </Text>
                          {content.translations.map((translation, index) => {
                            const locale = locales.find(l => l.id === translation.locale_id);
                            return (
                              <Menu key={index} placement="right-start">
                                <MenuButton as={MenuItem} icon={<FiCopy />}>
                                  From {locale?.name || 'Unknown'}
                                </MenuButton>
                                <MenuList>
                                  {availableLocalesForTranslation.map(targetLocale => (
                                    <MenuItem
                                      key={targetLocale.id}
                                      onClick={() => duplicateTranslation(index, targetLocale.id)}
                                    >
                                      To {targetLocale.name} ({targetLocale.code})
                                    </MenuItem>
                                  ))}
                                </MenuList>
                              </Menu>
                            );
                          })}
                        </>
                      )}
                    </MenuList>
                  </Menu>
                )}
              </Flex>

              {content.translations.length === 0 ? (
                <Alert status="warning">
                  <AlertIcon />
                  No translations available. At least one translation is required.
                </Alert>
              ) : (
                <VStack spacing={6} align="stretch">
                  {content.translations.map((translation, index) => (
                    <Card key={`${translation.locale_id}-${index}`} borderColor={borderColor} variant="outline">
                      <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
                        <HStack justify="space-between">
                          <HStack spacing={3}>
                            <Heading size="sm">{getLocaleName(translation.locale_id)}</Heading>
                            <Badge colorScheme={index === 0 ? 'green' : 'blue'}>
                              {index === 0 ? 'Primary' : 'Translation'}
                            </Badge>
                            <Badge colorScheme="gray" variant="outline">
                              {translation.status || 'draft'}
                            </Badge>
                          </HStack>
                          {content.translations.length > 1 && (
                            <Menu>
                              <MenuButton as={IconButton} icon={<ChevronDownIcon />} variant="ghost" size="sm" />
                              <MenuList>
                                <MenuItem
                                  icon={<FiTrash2 />}
                                  onClick={() => {
                                    setTranslationToDelete(index);
                                    onDeleteAlertOpen();
                                  }}
                                  color="red.500"
                                >
                                  Remove Translation
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          )}
                        </HStack>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4} align="stretch">
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl isRequired={index === 0} isInvalid={errors.title && index === 0}>
                              <FormLabel fontSize="sm">Title</FormLabel>
                              <Input
                                value={translation.title || ''}
                                onChange={e => handleTranslationChange(index, 'title', e.target.value)}
                                placeholder="Enter title"
                              />
                              {errors.title && index === 0 && (
                                <Text color="red.500" fontSize="sm" mt={1}>{errors.title}</Text>
                              )}
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="sm">Translation Status</FormLabel>
                              <Select
                                value={translation.status || 'draft'}
                                onChange={e => handleTranslationChange(index, 'status', e.target.value)}
                              >
                                <option value="draft">Draft</option>
                                <option value="published">Published</option>
                                <option value="archived">Archived</option>
                              </Select>
                            </FormControl>
                          </SimpleGrid>

                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl>
                              <FormLabel fontSize="sm">Content</FormLabel>
                              <Textarea
                                value={translation.content || ''}
                                onChange={e => handleTranslationChange(index, 'content', e.target.value)}
                                placeholder="Enter main content"
                                minH="150px"
                                resize="vertical"
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="sm">Excerpt</FormLabel>
                              <Textarea
                                value={translation.excerpt || ''}
                                onChange={e => handleTranslationChange(index, 'excerpt', e.target.value)}
                                placeholder="Enter excerpt"
                                minH="100px"
                                resize="vertical"
                              />
                            </FormControl>
                          </SimpleGrid>

                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                            <FormControl>
                              <FormLabel fontSize="sm">Meta Title</FormLabel>
                              <Input
                                value={translation.meta_title || ''}
                                onChange={e => handleTranslationChange(index, 'meta_title', e.target.value)}
                                placeholder="Enter meta title"
                              />
                            </FormControl>

                            <FormControl>
                              <FormLabel fontSize="sm">Meta Description</FormLabel>
                              <Textarea
                                value={translation.meta_description || ''}
                                onChange={e => handleTranslationChange(index, 'meta_description', e.target.value)}
                                placeholder="Enter meta description"
                                resize="vertical"
                              />
                            </FormControl>
                          </SimpleGrid>

                          {/* Custom Fields */}
                          {modelFields.length > 0 && (
                            <Box>
                              <Divider my={4} />
                              <Heading size="sm" mb={4}>Custom Fields</Heading>
                              <Accordion allowMultiple>
                                {modelFields.map(field => (
                                  <AccordionItem key={field.key} borderWidth="1px" borderRadius="md" mb={3}>
                                    <h2>
                                      <AccordionButton py={3}>
                                        <HStack flex="1" textAlign="left" spacing={3}>
                                          <Text fontWeight="medium">{field.label}</Text>
                                          <Badge colorScheme="blue" fontSize="xs">{field.type}</Badge>
                                        </HStack>
                                        <AccordionIcon />
                                      </AccordionButton>
                                    </h2>
                                    <AccordionPanel pb={4}>
                                      <FormControl>
                                        <FormLabel fontSize="sm" fontWeight="medium">{field.label}</FormLabel>
                                        {renderCustomField(field, index)}
                                      </FormControl>
                                    </AccordionPanel>
                                  </AccordionItem>
                                ))}
                              </Accordion>
                            </Box>
                          )}
                        </VStack>
                      </CardBody>
                    </Card>
                  ))}
                </VStack>
              )}
            </VStack>
          </TabPanel>

          {/* Taxonomies Tab */}
          {modelTaxonomies.length > 0 && (
            <TabPanel>
              <VStack spacing={5} align="stretch">
                <HStack justify="space-between">
                  <Heading size="md">Taxonomies</Heading>
                  <Badge colorScheme="blue" variant="outline">
                    {Object.keys(selectedTerms).length} taxonomies configured
                  </Badge>
                </HStack>

                {loadingTaxonomies ? (
                  <Flex justify="center" py={10}>
                    <Spinner />
                    <Text ml={3}>Loading taxonomies...</Text>
                  </Flex>
                ) : (
                  <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
                    {modelTaxonomies.map(taxonomy => {
                      // FIXED: More robust data access with multiple key formats
                      const termsForTaxonomy = taxonomyTerms[taxonomy.id] || [];
                      
                      // FIXED: Try multiple key formats for selectedTerms
                      const selectedTermsForTaxonomy = 
                        selectedTerms[taxonomy.id] || 
                        selectedTerms[taxonomy.id.toString()] || 
                        selectedTerms[parseInt(taxonomy.id)] || 
                        [];

                      console.log('Debug taxonomy data:', {
                        taxonomyId: taxonomy.id,
                        taxonomyName: taxonomy.name,
                        selectedTermsKeys: Object.keys(selectedTerms),
                        selectedTermsForThisTaxonomy: selectedTermsForTaxonomy,
                        termsCount: termsForTaxonomy.length
                      });

                      return (
                        <Card key={taxonomy.id} borderColor={borderColor} variant="outline">
                          <CardHeader bg={headerBg} borderBottomWidth="1px" borderColor={borderColor}>
                            <VStack align="start" spacing={2}>
                              <HStack justify="space-between" w="full">
                                <Heading size="sm">{taxonomy.name}</Heading>
                                <Badge colorScheme="blue" variant="subtle">
                                  {selectedTermsForTaxonomy.length} selected
                                </Badge>
                              </HStack>
                              {taxonomy.description && (
                                <Text fontSize="xs" color="gray.500">{taxonomy.description}</Text>
                              )}
                              {/* DEBUG: Show taxonomy ID and selected terms structure */}
                              <Text fontSize="xs" color="gray.400">
                                ID: {taxonomy.id} | Selected: {JSON.stringify(selectedTermsForTaxonomy)}
                              </Text>
                            </VStack>
                          </CardHeader>
                          <CardBody>
                            {termsForTaxonomy.length > 0 ? (
                              <>
                                {/* Show selected terms */}
                                {selectedTermsForTaxonomy.length > 0 && (
                                  <Box mb={4}>
                                    <Text fontSize="sm" mb={2} fontWeight="medium" color="blue.600">
                                      Selected Terms:
                                    </Text>
                                    <Flex wrap="wrap" gap={2}>
                                      {selectedTermsForTaxonomy.map(termId => {
                                        const term = termsForTaxonomy.find(t => 
                                          t.id.toString() === termId.toString() ||
                                          t.id === termId ||
                                          parseInt(t.id) === parseInt(termId)
                                        );
                                        return term ? (
                                          <Badge key={termId} colorScheme="blue" py={1} px={2} variant="solid">
                                            {getTermDisplayName(term)}
                                          </Badge>
                                        ) : (
                                          <Badge key={termId} colorScheme="red" py={1} px={2} variant="outline">
                                            Unknown term: {termId}
                                          </Badge>
                                        );
                                      })}
                                    </Flex>
                                    <Divider mt={3} />
                                  </Box>
                                )}

                                {/* Terms selection */}
                                <Box>
                                  <Text fontSize="sm" mb={3} fontWeight="medium">
                                    Available Terms ({termsForTaxonomy.length}):
                                  </Text>
                                  <CheckboxGroup
                                    colorScheme="blue"
                                    value={(selectedTerms[taxonomy.id.toString()] || [])}
                                    onChange={values => {
                                      console.log('CheckboxGroup onChange:', {
                                        taxonomyId: taxonomy.id,
                                        newValues: values,
                                        oldValues: selectedTermsForTaxonomy
                                      });
                                      handleTermsChange(taxonomy.id, values);
                                    }}
                                  >
                                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                                      {termsForTaxonomy.map(term => (
                                        <Box key={term.id} p={2} borderRadius="md" _hover={{ bg: 'gray.50' }}>
                                          <Checkbox value={term.id.toString()} colorScheme="blue">
                                            <VStack align="start" spacing={1}>
                                              <Text fontSize="sm" fontWeight="medium">
                                                {getTermDisplayName(term)}
                                              </Text>
                                              {getTermDisplayName(term) !== term.name && (
                                                <Text fontSize="xs" color="gray.500">({term.name})</Text>
                                              )}
                                              {term.translations && term.translations.length > 1 && (
                                                <Badge size="sm" colorScheme="green" variant="subtle">
                                                  {term.translations.length} translations
                                                </Badge>
                                              )}
                                              {/* DEBUG: Show term ID */}
                                              <Text fontSize="xs" color="gray.400">ID: {term.id}</Text>
                                            </VStack>
                                          </Checkbox>
                                        </Box>
                                      ))}
                                    </SimpleGrid>
                                  </CheckboxGroup>
                                </Box>
                              </>
                            ) : (
                              <Alert status="info" variant="subtle">
                                <AlertIcon />
                                <VStack align="start" spacing={1}>
                                  <Text fontSize="sm">No terms available for this taxonomy</Text>
                                  <Text fontSize="xs" color="gray.500">
                                    Add terms to this taxonomy to enable content classification
                                  </Text>
                                </VStack>
                              </Alert>
                            )}
                          </CardBody>
                        </Card>
                      );
                    })}
                  </SimpleGrid>
                )}
              </VStack>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>

      {/* Add Translation Modal */}
      <Modal isOpen={isTranslationModalOpen} onClose={onTranslationModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add New Translation</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text color="gray.500" fontSize="sm">
                Select a language to add a new translation for this content.
              </Text>

              <FormControl>
                <FormLabel>Language</FormLabel>
                <Select
                  placeholder="Select language..."
                  value={selectedLocaleForNewTranslation}
                  onChange={e => setSelectedLocaleForNewTranslation(e.target.value)}
                >
                  {availableLocalesForTranslation.map(locale => (
                    <option key={locale.id} value={locale.id}>
                      {locale.name} ({locale.code})
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTranslationModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={() => addTranslation(selectedLocaleForNewTranslation)}
              isDisabled={!selectedLocaleForNewTranslation}
            >
              Add Translation
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Translation Alert Dialog */}
      <AlertDialog isOpen={isDeleteAlertOpen} leastDestructiveRef={cancelRef} onClose={onDeleteAlertClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Remove Translation
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to remove this translation? This action cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteAlertClose}>
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={() => {
                  removeTranslation(translationToDelete);
                  onDeleteAlertClose();
                  setTranslationToDelete(null);
                }}
                ml={3}
              >
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Bottom Action Bar */}
      <Flex justify="flex-end" mt={8} mb={4} borderTopWidth="1px" pt={5}>
        <HStack spacing={4}>
          <Button variant="outline" as={RouterLink} to="/content">
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={saving}
            loadingText="Saving..."
            leftIcon={<FiSave />}
            isDisabled={
              !content.site_id ||
              !content.content_model_id ||
              !content.slug ||
              content.translations.length === 0 ||
              !primaryTranslation?.locale_id ||
              !primaryTranslation?.title
            }
          >
            Save Changes
          </Button>
        </HStack>
      </Flex>
    </Container>
  );
};

export default EditContent;
